import { MOCK_AUCTION_SEEDS } from "@/mocks/auctions";
import { getGroupMockSnapshot, recordWonCycle } from "@/services/groups";
import { listMembers } from "@/services/members";
import { USE_MOCK_API } from "@/services/config";
import { http } from "@/services/http";
import {
  ApiError,
  type Auction,
  type AuctionBid,
  type AuctionEligibilityReason,
  type AuctionEligibleMember,
  type AuctionIneligibleMember,
  type AuctionResult,
  type AuctionSeed,
} from "@/services/types";
import { computeAuctionResult } from "@/utils/auction";
import { cyclePeriodLabel } from "@/utils/group";
import { formatINRNumber } from "@/utils/format";

/**
 * Centralized auction service. Every auction screen calls through here — no
 * screen performs a direct data fetch. In real-API mode, the authoritative
 * result is computed by the backend when an auction closes (Stage 5/6); in
 * mock mode it's computed here instead, matching the same shape.
 */

const delay = (ms = 350) => new Promise((resolve) => setTimeout(resolve, ms));

/** In-memory store seeded from mock auction records — used only when USE_MOCK_API is true. */
let store: AuctionSeed[] = [...MOCK_AUCTION_SEEDS];

function findSeed(groupId: string, cycle: number): AuctionSeed | undefined {
  return store.find((s) => s.groupId === groupId && s.cycle === cycle);
}

/* ================= Real API — backend shapes and mapping ================= */

interface BackendGroup {
  id: string;
  groupCode: string;
  groupName: string;
  installmentAmount: string;
  totalMembers: number;
  bidStepAmount: string;
  minDiscountPercent: string | null;
  maxDiscountPercent: string | null;
  blockBidIfOverdue: boolean;
  dividendApplyTo: string;
}

interface BackendGroupMemberRow {
  groupMemberId: string;
  memberId: string;
  fullName: string;
  ticketNo: number;
  isOrganizer: boolean;
}

interface BackendGroupCycleRow {
  id: string;
  cycleNo: number;
  kind: string;
  status: string;
}

interface BackendDueEntry {
  cycleId: string;
  groupId: string;
  groupCode: string;
  groupName: string;
  cycleNo: number;
  kind: string;
  periodLabel: string;
  auctionDate: string | null;
  status: string;
  grossPool: string;
  totalMembers: number;
  eligibleCount?: number;
  organizerName?: string | null;
  organizerTicketNo?: string | null;
}

interface BackendCycleDetail {
  id: string;
  groupId: string;
  groupCode: string;
  groupName: string;
  totalMembers: number;
  installmentAmount: string;
  cycleNo: number;
  kind: string;
  periodLabel: string;
  auctionDate: string | null;
  grossPool: string;
  winner: { groupMemberId: string; memberId: string; fullName: string; ticketNo: number } | null;
  winningBidAmount: string | null;
  discountAmount: string;
  dividendPerMember: string;
  roundingAdjustment: string;
  netInstallment: string | null;
  expectedCollection: string | null;
  status: string;
  auctionClosedAt: string | null;
  closedByName: string | null;
  payout: { voucherNo: string; prizeAmount: string; duesAdjusted: string; otherDeductions: string; netPayable: string; status: string } | null;
}

interface BackendEligibleRow {
  groupMemberId: string;
  memberId: string;
  memberCode: string;
  fullName: string;
  phone: string;
  ticketNo: number;
  outstanding: string;
}

interface BackendIneligibleRow extends BackendEligibleRow {
  reason: string;
  reasonType: string;
  detail?: string;
}

interface BackendBid {
  id: string;
  memberId: string;
  bidAmount: string;
  isValid: boolean;
  placedAt: string;
  placedBy: string | null;
}

const CLOSED_STATUSES = new Set(["AUCTION_CLOSED", "COLLECTING", "PAYOUT_DONE", "CLOSED"]);

async function fetchGroupMembers(groupId: string): Promise<BackendGroupMemberRow[]> {
  return http<BackendGroupMemberRow[]>(`/groups/${groupId}/members`, { method: "GET" });
}

async function fetchCycleRow(groupId: string, cycle: number): Promise<BackendGroupCycleRow> {
  const rows = await http<BackendGroupCycleRow[]>(`/groups/${groupId}/cycles`, { method: "GET" });
  const row = rows.find((c) => c.cycleNo === cycle);
  if (!row) throw new ApiError("This cycle does not exist in the group.", 404, "CYCLE_NOT_FOUND");
  return row;
}

/**
 * Opens the auction the first time a still-PENDING cycle is loaded, so the
 * real state machine (PENDING -> AUCTION_OPEN) actually advances without
 * requiring a separate "open auction" screen the mock UI never had. A
 * CYCLE_OUT_OF_ORDER response means an earlier cycle isn't decided yet —
 * reported back as blockedOutOfOrder instead of surfacing as an error.
 *
 * INVALID_CYCLE_STATUS means the cycle is already past PENDING — not an
 * error, just someone (a concurrent tab, or React StrictMode's deliberate
 * double effect invocation in dev) already opened it a moment earlier.
 * Proceed exactly as if this call had opened it.
 */
async function tryOpenAuction(cycleId: string): Promise<boolean> {
  try {
    await http(`/cycles/${cycleId}/open-auction`, { method: "POST" });
    return true;
  } catch (err) {
    if (err instanceof ApiError && err.code === "CYCLE_OUT_OF_ORDER") return false;
    if (err instanceof ApiError && err.code === "INVALID_CYCLE_STATUS") return true;
    throw err;
  }
}

function mapEligible(m: BackendEligibleRow): AuctionEligibleMember {
  return {
    memberId: m.memberId,
    memberCode: m.memberCode,
    fullName: m.fullName,
    primaryPhone: m.phone,
    ticketNumber: String(m.ticketNo),
    outstanding: Number(m.outstanding),
    groupMemberId: m.groupMemberId,
  };
}

function mapIneligible(m: BackendIneligibleRow): AuctionIneligibleMember {
  return {
    memberId: m.memberId,
    memberCode: m.memberCode,
    fullName: m.fullName,
    primaryPhone: m.phone,
    ticketNumber: String(m.ticketNo),
    reason: m.reason,
    reasonType: m.reasonType as AuctionEligibilityReason,
    detail: m.detail,
  };
}

function mapBid(b: BackendBid): AuctionBid {
  return {
    id: b.id,
    memberId: b.memberId,
    amount: Number(b.bidAmount),
    placedAt: b.placedAt,
    placedBy: b.placedBy ?? "Staff",
    status: b.isValid ? "standing" : "withdrawn",
  };
}

function mapAuctionDetail(params: {
  detail: BackendCycleDetail;
  group: BackendGroup;
  eligible: BackendEligibleRow[];
  ineligible: BackendIneligibleRow[];
  bids: BackendBid[];
  members: BackendGroupMemberRow[];
  blockedOutOfOrder: boolean;
}): Auction {
  const { detail, group, eligible, ineligible, bids, members, blockedOutOfOrder } = params;
  const organizerRow = members.find((m) => m.isOrganizer);
  const isClosed = CLOSED_STATUSES.has(detail.status);

  const result: AuctionResult | null =
    isClosed && detail.winner
      ? {
          winnerMemberId: detail.winner.memberId,
          winningBid: Number(detail.winningBidAmount ?? 0),
          discount: Number(detail.discountAmount),
          perMemberDividend: Number(detail.dividendPerMember),
          dividendRemainder: Number(detail.roundingAdjustment),
          netInstallment: Number(detail.netInstallment ?? detail.installmentAmount),
          totalCollected: Number(detail.expectedCollection ?? 0),
          winnerPendingDues: detail.payout ? Number(detail.payout.duesAdjusted) : 0,
          winnerPayout: detail.payout ? Number(detail.payout.netPayable) : 0,
          closedAt: detail.auctionClosedAt ?? "",
          closedBy: detail.closedByName ?? "Staff",
        }
      : null;

  return {
    id: detail.id,
    groupId: detail.groupId,
    groupName: detail.groupName,
    groupCode: detail.groupCode,
    cycle: detail.cycleNo,
    periodLabel: detail.periodLabel,
    scheduledDate: detail.auctionDate ?? "",
    pool: Number(detail.grossPool),
    monthlyInstallment: Number(detail.installmentAmount),
    memberCount: detail.totalMembers,
    isOrganizerReserved: detail.kind === "ORGANIZER_RESERVED",
    organizer: organizerRow
      ? { memberId: organizerRow.memberId, fullName: organizerRow.fullName, ticketNumber: String(organizerRow.ticketNo) }
      : null,
    bidStep: Number(group.bidStepAmount),
    minDiscountPct: group.minDiscountPercent != null ? Number(group.minDiscountPercent) : null,
    maxDiscountPct: group.maxDiscountPercent != null ? Number(group.maxDiscountPercent) : null,
    blockOverdueBidding: group.blockBidIfOverdue,
    dividendTiming: group.dividendApplyTo === "NEXT_CYCLE" ? "next_month" : "same_month",
    status: isClosed ? "closed" : "pending",
    eligible: eligible.map(mapEligible),
    ineligible: ineligible.map(mapIneligible),
    bids: bids.map(mapBid),
    result,
    blockedOutOfOrder,
  };
}

/**
 * Dashboard projection — /auctions/due only returns not-yet-decided cycles
 * (no "recently closed" feed exists at this stage; that belongs to the
 * auction-history report, a later stage), so the mock's "Recently closed"
 * dashboard section stays empty in real-API mode. Everything the dashboard
 * card actually renders (pool, eligible count, organizer name) is real.
 */
function buildDashboardAuction(entry: BackendDueEntry): Auction {
  const isClosed = CLOSED_STATUSES.has(entry.status);
  const eligibleCount = entry.eligibleCount ?? Math.max(0, entry.totalMembers - (entry.cycleNo - 1));
  const dummyEligible: Auction["eligible"] = Array.from({ length: eligibleCount }, (_, i) => ({
    groupMemberId: `placeholder-${i}`,
    memberId: `placeholder-m-${i}`,
    fullName: `Member ${i + 1}`,
    ticketNumber: String(i + 1),
    outstandingDues: 0,
  }));

  return {
    id: entry.cycleId,
    groupId: entry.groupId,
    groupName: entry.groupName,
    groupCode: entry.groupCode,
    cycle: entry.cycleNo,
    periodLabel: entry.periodLabel,
    scheduledDate: entry.auctionDate ?? "",
    pool: Number(entry.grossPool),
    monthlyInstallment: 0,
    memberCount: entry.totalMembers,
    isOrganizerReserved: entry.kind === "ORGANIZER_RESERVED",
    organizer: entry.organizerName
      ? { memberId: "", fullName: entry.organizerName, ticketNumber: entry.organizerTicketNo ?? "—" }
      : null,
    bidStep: 0,
    minDiscountPct: null,
    maxDiscountPct: null,
    blockOverdueBidding: false,
    dividendTiming: "same_month",
    status: isClosed ? "closed" : "pending",
    eligible: dummyEligible,
    ineligible: [],
    bids: [],
    result: null,
  };
}

/* ================= Mock — pure calculation over the seeded store ================= */

/**
 * Resolve a raw seed into a full auction view: pool, eligibility, current
 * standings, and (for decided cycles) the authoritative result.
 */
async function buildAuction(seed: AuctionSeed): Promise<Auction> {
  const group = getGroupMockSnapshot(seed.groupId);
  const members = await listMembers();
  const memberMap = new Map(members.map((m) => [m.id, m]));

  const isReserved = seed.cycle === group.reservedCycle;
  const won = group.wonCycles.find((w) => w.cycle === seed.cycle);

  const orgTicket = group.tickets.find((t) => t.memberId === group.organizerMemberId);
  const orgMember = memberMap.get(group.organizerMemberId);
  const organizer = orgMember
    ? { memberId: orgMember.id, fullName: orgMember.fullName, ticketNumber: orgTicket?.ticketNumber ?? "—" }
    : null;

  const eligible: Auction["eligible"] = [];
  const ineligible: Auction["ineligible"] = [];

  for (const ticket of group.tickets) {
    const m = memberMap.get(ticket.memberId);
    if (!m) continue;
    const base = {
      memberId: m.id,
      memberCode: m.memberCode,
      fullName: m.fullName,
      primaryPhone: m.primaryPhone,
      ticketNumber: ticket.ticketNumber,
    };

    if (isReserved) {
      ineligible.push({
        ...base,
        reason: "This is the reserved organizer cycle — no auction is held.",
        reasonType: "reserved",
      });
      continue;
    }

    const enrollment = m.enrollments.find((e) => e.groupId === group.id);
    const wonCycle =
      enrollment?.wonCycle ?? group.wonCycles.find((w) => w.memberId === m.id)?.cycle ?? null;

    if (wonCycle != null) {
      ineligible.push({
        ...base,
        reason: `Already won the pool in cycle ${wonCycle}.`,
        reasonType: "won",
        detail: String(wonCycle),
      });
      continue;
    }
    if (ticket.memberId === group.organizerMemberId) {
      ineligible.push({
        ...base,
        reason: "The organizer cannot bid — their cycle is reserved.",
        reasonType: "organizer",
      });
      continue;
    }
    if (m.status === "inactive" || m.status === "blacklisted") {
      ineligible.push({
        ...base,
        reason: "Enrolment is not active for this group.",
        reasonType: "inactive",
      });
      continue;
    }
    const outstanding = enrollment?.outstanding ?? 0;
    eligible.push({ ...base, outstanding });
  }

  // Cycles must close in order: block opening a later cycle while an earlier one
  // is still unresolved.
  const earlierDecided =
    seed.cycle > 1 ? group.wonCycles.filter((w) => w.cycle < seed.cycle).length : 0;
  const blockedOutOfOrder = seed.cycle > 1 && earlierDecided < seed.cycle - 1;

  const isClosed = won != null || seed.status === "closed";
  let result = seed.result ?? null;
  let bids = seed.bids ?? [];

  // Auto-generate a closed record for any decided cycle that has no stored seed,
  // so every historical cycle is reachable as an audit record.
  if (won && !result) {
    const winnerPending = memberMap.get(won.memberId)?.enrollments.find((e) => e.groupId === group.id)?.outstanding ?? 0;
    result = computeAuctionResult({
      pool: group.chitValue,
      monthlyInstallment: group.monthlyInstallment,
      memberCount: group.memberCount,
      dividendTiming: group.dividendTiming,
      winnerMemberId: won.memberId,
      winningBid: won.winningBid,
      winnerPendingDues: winnerPending,
      closedBy: group.updatedBy,
      closedAt: group.updatedAt,
    });
  }

  return {
    id: seed.id,
    groupId: group.id,
    groupName: group.name,
    groupCode: group.groupCode,
    cycle: seed.cycle,
    periodLabel: cyclePeriodLabel(group.startDate, seed.cycle),
    scheduledDate: seed.scheduledDate,
    pool: group.chitValue,
    monthlyInstallment: group.monthlyInstallment,
    memberCount: group.memberCount,
    isOrganizerReserved: isReserved,
    organizer,
    bidStep: group.bidStep,
    minDiscountPct: group.minDiscountPct,
    maxDiscountPct: group.maxDiscountPct,
    blockOverdueBidding: group.blockOverdueBidding,
    dividendTiming: group.dividendTiming,
    commissionType: group.commissionType,
    commissionValue: group.commissionValue,
    status: isClosed ? "closed" : "pending",
    eligible,
    ineligible,
    bids,
    result,
    blockedOutOfOrder,
  };
}

/* ================= Exported service ================= */

/** Every auction (pending and closed), oldest schedule first. */
export async function listAuctions(): Promise<Auction[]> {
  if (!USE_MOCK_API) {
    const due = await http<{ dueToday: BackendDueEntry[]; overdue: BackendDueEntry[]; upcoming: BackendDueEntry[]; recentlyClosed?: BackendDueEntry[] }>(
      "/auctions/due",
      { method: "GET" }
    );
    const allPending = [...due.dueToday, ...due.overdue, ...due.upcoming];
    const recentlyClosed = due.recentlyClosed ?? [];
    const all = [...allPending, ...recentlyClosed];

    return all
      .map(buildDashboardAuction)
      .sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate));
  }

  await delay();
  const built = await Promise.all(store.map((s) => buildAuction(s)));
  return built.sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate));
}

/** A single auction for the console. Handles cycles without a stored seed. */
export async function getAuction(groupId: string, cycle: number): Promise<Auction> {
  if (!USE_MOCK_API) {
    const cycleRow = await fetchCycleRow(groupId, cycle);

    let blockedOutOfOrder = false;
    if (cycleRow.status === "PENDING" && cycleRow.kind === "AUCTION") {
      blockedOutOfOrder = !(await tryOpenAuction(cycleRow.id));
    }

    const [group, detail, eligibility, bids, members] = await Promise.all([
      http<BackendGroup>(`/groups/${groupId}`, { method: "GET" }),
      http<BackendCycleDetail>(`/cycles/${cycleRow.id}`, { method: "GET" }),
      http<{ eligible: BackendEligibleRow[]; ineligible: BackendIneligibleRow[] }>(`/cycles/${cycleRow.id}/eligible-members`, {
        method: "GET",
      }),
      http<BackendBid[]>(`/cycles/${cycleRow.id}/bids`, { method: "GET" }),
      fetchGroupMembers(groupId),
    ]);

    return mapAuctionDetail({
      detail,
      group,
      eligible: eligibility.eligible,
      ineligible: eligibility.ineligible,
      bids,
      members,
      blockedOutOfOrder,
    });
  }

  await delay(200);
  let seed = findSeed(groupId, cycle);
  if (!seed) {
    const group = getGroupMockSnapshot(groupId);
    if (cycle < 1 || cycle > group.memberCount) {
      throw new ApiError("This cycle does not exist in the group.", 404, "CYCLE_NOT_FOUND");
    }
    seed = { id: `${groupId}-c${cycle}`, groupId, cycle, scheduledDate: "", bids: [], status: "pending" };
  }
  return buildAuction(seed);
}

/**
 * Place a bid on behalf of a member. Real mode persists it server-side with
 * full section-11 validation; mock mode synthesizes the same shape locally.
 * `groupMemberId` is required in real mode — it comes from the eligible
 * member row returned by getAuction(), not from the member id alone.
 */
export async function placeBid(
  cycleId: string,
  member: { memberId: string; groupMemberId?: string },
  bidAmount: number
): Promise<AuctionBid> {
  if (!USE_MOCK_API) {
    if (!member.groupMemberId) {
      throw new ApiError("This member has no enrollment reference to bid with.", 422, "VALIDATION_ERROR");
    }
    const bid = await http<BackendBid>(`/cycles/${cycleId}/bids`, {
      method: "POST",
      body: { groupMemberId: member.groupMemberId, bidAmount },
    });
    return mapBid(bid);
  }

  await delay(150);
  return {
    id: `live-${Date.now()}`,
    memberId: member.memberId,
    amount: bidAmount,
    placedAt: new Date().toISOString(),
    placedBy: "Staff User",
    status: "standing",
  };
}

/** Withdraw a standing bid. No-op in mock mode — the bid only ever lived in local console state there. */
export async function withdrawBid(bidId: string): Promise<void> {
  if (!USE_MOCK_API) {
    await http<void>(`/bids/${bidId}`, { method: "DELETE" });
    return;
  }
  await delay(150);
}

/** Close an auction by its winning bid. Computes the authoritative result. */
export async function closeAuction(
  groupId: string,
  cycle: number,
  winningBid: number,
  bids: AuctionBid[],
  actor: string
): Promise<Auction> {
  if (!USE_MOCK_API) {
    const cycleRow = await fetchCycleRow(groupId, cycle);
    const winningBidRow = bids.find((b) => b.status === "standing" && Math.round(b.amount) === Math.round(winningBid));
    if (!winningBidRow) {
      throw new ApiError("No valid winning bid to close this auction with.", 409, "NO_WINNING_BID");
    }
    await http(`/cycles/${cycleRow.id}/close-auction`, {
      method: "POST",
      body: { winningBidId: winningBidRow.id },
      headers: { "Idempotency-Key": `close-auction-${cycleRow.id}-${Date.now()}-${Math.random().toString(36).slice(2)}` },
    });
    return getAuction(groupId, cycle);
  }

  await delay(500);
  const group = getGroupMockSnapshot(groupId);
  if (cycle === group.reservedCycle) {
    throw new ApiError("This is the reserved organizer cycle and cannot be closed by auction.", 409, "RESERVED_CYCLE");
  }
  if (group.wonCycles.some((w) => w.cycle === cycle)) {
    throw new ApiError("This cycle is already closed.", 409, "CYCLE_DONE");
  }
  const standing = bids.filter((b) => b.status === "standing");
  const lowest = standing.length ? [...standing].sort((a, b) => a.amount - b.amount)[0] : null;
  if (!lowest || Math.round(lowest.amount) !== Math.round(winningBid)) {
    throw new ApiError("No valid winning bid to close this auction with.", 409, "NO_WINNING_BID");
  }
  const winner = lowest.memberId;
  const members = await listMembers();
  const winnerPending =
    members.find((m) => m.id === winner)?.enrollments.find((e) => e.groupId === groupId)?.outstanding ?? 0;
  const closedAt = new Date().toISOString();
  const result = computeAuctionResult({
    pool: group.chitValue,
    monthlyInstallment: group.monthlyInstallment,
    memberCount: group.memberCount,
    dividendTiming: group.dividendTiming,
    winnerMemberId: winner,
    winningBid: lowest.amount,
    winnerPendingDues: winnerPending,
    closedBy: actor,
    closedAt,
  });

  await recordWonCycle(groupId, cycle, winner, lowest.amount, actor);

  const existing = findSeed(groupId, cycle);
  const updated: AuctionSeed = {
    id: existing?.id ?? `${groupId}-c${cycle}`,
    groupId,
    cycle,
    scheduledDate: existing?.scheduledDate ?? "",
    status: "closed",
    bids,
    result,
  };
  store = [...store.filter((s) => !(s.groupId === groupId && s.cycle === cycle)), updated];
  return buildAuction(updated);
}

/** Close the reserved organizer cycle: full pool, no discount, no bids. */
export async function closeReservedCycle(groupId: string, cycle: number, actor: string): Promise<Auction> {
  if (!USE_MOCK_API) {
    const cycleRow = await fetchCycleRow(groupId, cycle);
    await http(`/cycles/${cycleRow.id}/close-organizer-cycle`, {
      method: "POST",
      headers: { "Idempotency-Key": `close-organizer-${cycleRow.id}-${Date.now()}-${Math.random().toString(36).slice(2)}` },
    });
    return getAuction(groupId, cycle);
  }

  await delay(500);
  const group = getGroupMockSnapshot(groupId);
  if (cycle !== group.reservedCycle) {
    throw new ApiError("This is not the reserved organizer cycle.", 409, "NOT_RESERVED");
  }
  if (group.wonCycles.some((w) => w.cycle === cycle)) {
    throw new ApiError("This cycle is already closed.", 409, "CYCLE_DONE");
  }
  const winner = group.organizerMemberId;
  const closedAt = new Date().toISOString();
  const result = computeAuctionResult({
    pool: group.chitValue,
    monthlyInstallment: group.monthlyInstallment,
    memberCount: group.memberCount,
    dividendTiming: group.dividendTiming,
    winnerMemberId: winner,
    winningBid: group.chitValue,
    winnerPendingDues: 0,
    closedBy: actor,
    closedAt,
  });

  await recordWonCycle(groupId, cycle, winner, group.chitValue, actor);

  const existing = findSeed(groupId, cycle);
  const updated: AuctionSeed = {
    id: existing?.id ?? `${groupId}-c${cycle}`,
    groupId,
    cycle,
    scheduledDate: existing?.scheduledDate ?? "",
    status: "closed",
    bids: [],
    result,
  };
  store = [...store.filter((s) => !(s.groupId === groupId && s.cycle === cycle)), updated];
  return buildAuction(updated);
}

export const auctionService = {
  listAuctions,
  getAuction,
  placeBid,
  withdrawBid,
  closeAuction,
  closeReservedCycle,
};

export { ApiError };
